import React from 'react';
import Notify from '@components/Notify/ActionNotify';
import NiceModal from '@ebay/nice-modal-react';
import { toast } from 'react-toastify';
import { Table } from 'reactstrap';

const NOT_DELETED_PREVIEW_LIMIT = 2;
const SEE_MORE_NOTIFICATION_DURATION = 8000;
const DELETE_NOTIFY_TOAST_ID = 'only-once';

// Backend reason codes meaning "the rules don't allow this delete" — nothing
// went wrong, so these are reported as a warning instead of an error.
const BLOCKED_REASON_CODES = ['related_record', 'not_deletable', 'preflight_failed'];

/**
 * Normalizes delete response payloads from Axios responses or socket payloads into a uniform shape.
 * Returns null if the source object does not match a delete result payload.
 */
export const normalizeDeleteResult = (source) => {
  if (!source || typeof source !== 'object') return null;

  // Axios response wrapper (`res.data`) vs flat/direct payload
  const root = source.data && typeof source.data === 'object' && !Array.isArray(source.data)
    ? source.data
    : source;

  // Inner data object (`res.data.data` or `payload.data`)
  const innerData = root.data && typeof root.data === 'object' && !Array.isArray(root.data)
    ? root.data
    : root;

  const deletedCount = innerData.deleted_count ?? root.deleted_count;
  const failedCount = innerData.failed_count ?? root.failed_count;
  const notDeleted = innerData.not_deleted ?? root.not_deleted;
  const status = root.status ?? innerData.status;
  const messages = root.messages ?? innerData.messages;
  const error = root.error ?? innerData.error;

  const hasCounts = deletedCount !== undefined || failedCount !== undefined;
  const hasNotDeleted = Array.isArray(notDeleted);

  if (!hasCounts && !hasNotDeleted && !status && !messages && !error) {
    return null;
  }

  return {
    status,
    messages,
    error,
    deleted_count: Number(deletedCount ?? 0),
    failed_count: Number(failedCount ?? 0),
    not_deleted: hasNotDeleted ? notDeleted : []
  };
};

/**
 * Determines whether a normalized delete result contains failures.
 */
export const hasDeleteFailures = (result) => {
  if (!result) return false;
  return (
    (Array.isArray(result.not_deleted) && result.not_deleted.length > 0) ||
    Number(result.failed_count ?? 0) > 0 ||
    result.status === 'failed'
  );
};

/**
 * True when every failure is a record the rules protect, so the result should be
 * shown as a warning rather than an error.
 */
export const isBlockedDeleteResult = (result) =>
  result?.status !== 'failed' &&
  Array.isArray(result?.not_deleted) &&
  result.not_deleted.length > 0 &&
  result.not_deleted.every((item) => BLOCKED_REASON_CODES.includes(item?.reason_code));

const NotDeletedItems = ({ items = [] }) => (
  <Table responsive size="sm" bordered className="mb-0 mt-50">
    <thead>
      <tr>
        <th>Record</th>
        <th>Reason</th>
      </tr>
    </thead>
    <tbody>
      {items.map((item, idx) => (
        <tr key={item?.id ?? idx}>
          <td>{item?.label || item?.name || 'Record'}</td>
          <td className="text-danger">{item?.reason || 'Reason unknown'}</td>
        </tr>
      ))}
    </tbody>
  </Table>
);

const NotDeletedPreview = ({ items = [] }) => (
  <ul className="ps-1 mb-0 mt-25">
    {items.map((item, idx) => (
      <li key={item?.id ?? idx} className="text-danger small mt-25">
        {item?.label || item?.name || 'Record'}: {item?.reason || 'Reason unknown'}
      </li>
    ))}
  </ul>
);

const showNotDeletedDetails = (items) => {
  NiceModal.show('dynamic-modal', {
    title: 'Records Not Deleted',
    prompt: false,
    content: (
      <div className="px-50 py-25">
        <NotDeletedItems items={items} />
      </div>
    )
  });
};

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const buildNotifyText = (result, buttonId) => {
  const baseMessage = Array.isArray(result?.messages)
    ? result.messages.join(' ')
    : result?.messages || result?.error || (result?.status === 'failed'
      ? 'Delete operation failed.'
      : 'Delete operation completed.');
  const notDeleted = result?.not_deleted || [];
  const previewItems = notDeleted.slice(0, NOT_DELETED_PREVIEW_LIMIT);
  const reasons = previewItems.map((item) => {
    const label = item?.label || item?.name;
    return `<li class="text-danger small mt-25">${escapeHtml(label || 'Record')}: ${escapeHtml(item?.reason || 'Reason unknown')}</li>`;
  }).join('');
  const remainingCount = notDeleted.length - previewItems.length;
  const seeMore = remainingCount > 0
    ? `<button id="${buttonId}" type="button" class="btn btn-sm btn-link p-0 mt-50">See details</button>`
    : '';

  return `<div class="d-flex flex-column text-start"><span>${escapeHtml(baseMessage)}</span><ul class="ps-1 mb-0 mt-25">${reasons}</ul>${seeMore}</div>`;
};

/**
 * Builds the display message or JSX element for a delete operation result.
 */
export const buildDeleteResultMessage = (result) => {
  if (!result) return '';

  const baseMessage = (() => {
    if (Array.isArray(result?.messages)) return result.messages.join(' ');
    if (typeof result?.messages === 'string' && result.messages.trim()) return result.messages;
    if (typeof result?.error === 'string' && result.error.trim()) return result.error;
    return result?.status === 'failed' ? 'Delete operation failed.' : 'Delete operation completed.';
  })();

  const notDeleted = result?.not_deleted;
  if (Array.isArray(notDeleted) && notDeleted.length > 0) {
    const previewItems = notDeleted.slice(0, NOT_DELETED_PREVIEW_LIMIT);
    const remainingCount = notDeleted.length - previewItems.length;

    return (
      <div className="d-flex flex-column text-start">
        <span>{baseMessage}</span>
        <NotDeletedPreview items={previewItems} />
        {remainingCount > 0 && (
          <button
            type="button"
            className="btn btn-sm btn-link align-self-start p-0 mt-50 d-flex align-items-center"
            onClick={() => showNotDeletedDetails(notDeleted)}
          >
            See details
          </button>
        )}
      </div>
    );
  }

  return baseMessage;
};

/**
 * Normalizes a delete response and displays an appropriate success or error toast.
 * Returns true if a toast was displayed, false if the payload was not delete-shaped.
 */
export const notifyDeleteResult = (res, { title } = {}) => {
  const result = normalizeDeleteResult(res);
  if (!result) return false;

  const message = buildDeleteResultMessage(result);
  if (hasDeleteFailures(result)) {
    const hasMoreReasons = result.not_deleted.length > NOT_DELETED_PREVIEW_LIMIT;
    const buttonId = hasMoreReasons ? `delete-reasons-${Date.now()}` : '';
    Notify.Custom({
      type: isBlockedDeleteResult(result) ? 'warning' : 'error',
      title: title || (isBlockedDeleteResult(result) ? 'Warning' : 'Error'),
      text: buildNotifyText(result, buttonId)
    });
    if (hasMoreReasons) {
      toast.update(DELETE_NOTIFY_TOAST_ID, { autoClose: SEE_MORE_NOTIFICATION_DURATION });
      setTimeout(() => {
        document.getElementById(buttonId)?.addEventListener(
          'click',
          () => showNotDeletedDetails(result.not_deleted),
          { once: true }
        );
      }, 0);
    }
  } else {
    Notify.Success({ title, message });
  }
  return true;
};
